import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react/hooks"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import { evictAffectedPrices } from "shared/api/price/evictPriceCache"
import { invalidateAffectedPrices } from "shared/api/price/modifyPriceCache"
import { mapAPITypeToTypename } from "shared/api/serializer/mapApiTypeToTypename"
import { mapTypenameToAPIType } from "shared/api/serializer/mapTypenameToAPIType"
import { MutationSchema } from "shared/api/types"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ProductPosition } from "shared/types/ProductPosition"
import {
  ProductPositionTypename,
  ProjectParentTypename,
  ProjectsAPIType
} from "shared/types/Project"
import {
  CREATE_PRODUCT_POSITION,
  CREATE_PROJECT_CHILD
} from "shared/types/ProjectMutations"
import { ProductPositionAddon } from "shared/types/__generated__/graphql"
import {
  CreateProductPositionInputSchema,
  ProductPositionSchema as ProductPositionResponseSchema
} from "shared/types/__generated__/validation"
import { InvalidDataError } from "shared/types/error/InvalidDataError"

import { useProductConfigurationQueryWithCacheLazy } from "entities/dim-configuration/api/useProductConfigurationQueryWithCache"
import { getProductConfigurationQueryVariables } from "entities/dim-configuration/lib/getProductConfigurationQueryVariables"
import { getProductPositionAddonsFromConfiguration } from "entities/dim-configuration/lib/getProductPositionAddonsFromConfiguration"
import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import {
  CreateProductPositionDocument,
  CreateProductPositionMutation,
  CreateProductPositionMutationVariables
} from "entities/product-positions/graphql/mutations.generated"
import { useProductPositionProductInformationLazyQuery } from "entities/product-positions/graphql/queries.generated"
import { getProductType } from "entities/products/lib/getProductType"
import { insertProjectChild } from "entities/project-children/lib/insertProjectChild"
import {
  useProjectChildrenLazyQuery,
  useProjectPermissionsLazyQuery
} from "entities/projects/graphql/queries.generated"
import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { useProjectStoreActions } from "features/project-management/model/store/useProjectStoreActions"
import z from "zod"

import { evictProductPosition } from "../lib/evictProductPosition"
import { prepareProductPositionCreate } from "../model/prepareProductPositionCreate"
import { CreateProductPositionParams } from "../types/ProductPosition"
import { useCreateFavorite } from "./useCreateFavorite"

const CreateProductPositionVariablesSchema = z.object({
  parentID: z.string(),
  parentType: z.string(),
  input: CreateProductPositionInputSchema().extend({ dimensioning: z.any() })
})

const CreateProductPositionResponseSchema = MutationSchema.extend({
  productPosition: ProductPositionResponseSchema()
})

export function useCreateProductPosition() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchProject] = useProjectChildrenLazyQuery()
  const [fetchPermissions] = useProjectPermissionsLazyQuery()
  const [fetchConfiguration] = useProductConfigurationQueryWithCacheLazy()
  const [fetchProductInformation] =
    useProductPositionProductInformationLazyQuery()
  const [createFavorite] = useCreateFavorite()
  const { setElementPending, unsetElementPending } = useProjectStoreActions()

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.ProductPosition.CREATE_PRODUCT_POSITION.Start"),
      success: t("Request.ProductPosition.CREATE_PRODUCT_POSITION.Success"),
      failure: t("Request.ProductPosition.CREATE_PRODUCT_POSITION.Failure")
    }
  })

  const startProjectRequest = useProjectRequestMonitor()

  const [mutate, rest] = useApolloMutation<
    CreateProductPositionMutation,
    CreateProductPositionMutationVariables
  >(CreateProductPositionDocument, {
    update: (cache, result, options) => {
      if (result.data?.productPosition && options.variables) {
        insertProjectChild({
          child: result.data.productPosition,
          position: result.data.productPosition.position,
          parentID: options.variables.parentID,
          parentType: mapAPITypeToTypename(
            options.variables.parentType
          ) as ProjectParentTypename,
          cache
        })
      }
    }
  })

  const createProductPosition = useEvent(
    async ({
      projectID,
      parentID,
      displayPosition,
      productPosition: positionData
    }: {
      projectID: string
      parentID?: string
      displayPosition?: number
      productPosition: CreateProductPositionParams
    }) => {
      const productPosition: CreateProductPositionParams = {
        ...positionData,
        references: positionData?.references?.map(reference => ({
          ...reference,
          id: generateUUID()
        }))
      }

      const productType = getProductType(productPosition.variantKey)
      const isFavorite = projectID === FAVORITES_PROJECT_ID

      if (isFavorite) {
        return createFavorite({ productPosition })
      }

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()

      let addons: ProductPositionAddon[] = []
      if (productPosition.configurationKey) {
        const configuration = await fetchConfiguration({
          variables: getProductConfigurationQueryVariables({
            productType,
            configurationKey: productPosition.configurationKey
          })
        })

        addons = getProductPositionAddonsFromConfiguration(
          configuration.data?.productConfiguration
        )
      }

      await fetchProject({ variables: { id: projectID } })

      if (productPosition.price != null || productPosition.priceGroup != null) {
        const { data: permissionsData } = await fetchPermissions({
          variables: { projectID }
        })
        const permissions = permissionsData?.projectPermissions?.permissions
        if (!permissions?.[ProjectPermissionType.EDIT_PRICE]) {
          productPosition.price = undefined
        }
        if (!permissions?.[ProjectPermissionType.EDIT_PRICE_GROUP]) {
          productPosition.priceGroup = undefined
        }
      }

      const { input, optimisticResponse, parentType } =
        prepareProductPositionCreate({
          projectID,
          parentID,
          displayPosition,
          productPosition,
          addons,
          cache: apolloCache
        })

      if (!input) {
        const error = new InvalidDataError(
          "failed to prepare product position input",
          { data: productPosition }
        )
        error.notify()
        raiseErrorToast()
        return { data: null, errors: [{ message: error.message }] }
      }

      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: CREATE_PRODUCT_POSITION
      })

      setElementPending({ itemID: input.id, projectID })

      emitAppEvent(CREATE_PROJECT_CHILD, {
        id: input.id,
        projectID,
        parentID: parentID || projectID,
        parentType: mapTypenameToAPIType(parentType) || ProjectsAPIType,
        type: ProductPositionTypename
      })

      invalidateAffectedPrices({
        projectID,
        itemID: parentID || projectID,
        itemType: mapAPITypeToTypename(
          mapTypenameToAPIType(parentType) || ProjectsAPIType
        ) as ProjectParentTypename,
        includeChild: true,
        cache: apolloCache
      })

      const result = await callMutation<
        CreateProductPositionMutation,
        CreateProductPositionMutationVariables,
        ProductPosition
      >(mutate, {
        variables: {
          parentID: parentID || projectID,
          parentType: mapTypenameToAPIType(parentType) || ProjectsAPIType,
          input
        },
        transform: data => data.productPosition,
        schemas: {
          input: CreateProductPositionVariablesSchema,
          response: CreateProductPositionResponseSchema
        },
        apolloOptions: {
          optimisticResponse,
          onCompleted: () => {
            fetchProductInformation({ variables: { id: input.id } })
            unsetElementPending({ itemID: input.id, projectID })
            evictAffectedPrices({
              projectID,
              itemID: parentID || projectID,
              itemType: mapAPITypeToTypename(
                mapTypenameToAPIType(parentType) || ProjectsAPIType
              ) as ProjectParentTypename,
              cache: apolloCache
            })
          },
          onError: () => {
            evictProductPosition(productPosition.id, apolloCache)
            unsetElementPending({ itemID: input.id, projectID })
          }
        }
      })

      completeProjectRequest()

      if (result.errors?.length) {
        raiseErrorToast()
      } else {
        raiseSuccessToast()
      }

      return result
    }
  )

  return [createProductPosition, rest] as [
    typeof createProductPosition,
    typeof rest
  ]
}
